import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { noop } from 'lodash-es';
import { Button, DataTable, DropdownMenu, DropdownMenuItem, FilterPickerGroup, Page, PageContentHeader, PageEmptyState, PageErrorState, Row } from 'seagull';
import { ChevronDown, Download, Import } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useOpenCreateClientModal } from '~/components/CreateClientModal/hooks/useOpenCreateClientModal';
import { useOpenEditClientModal } from '~/components/EditClientModal/hooks/useOpenEditClientModal';
import PageLoadingState from '~/components/PageLoadingState';
import TableFilters from '~/components/TableFilters/TableFilters';
import { routes } from '~/constants';
import { useClients } from '~/hooks/reactQuery/queries/useClients';
import { useClientArchiveRestoreDelete } from '~/hooks/useClientArchiveRestoreDelete';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useStateFromUrl } from '~/hooks/useStateFromUrl';
import { useTableColumnMenu } from '~/hooks/useTableColumnMenu';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { ClientDetailPage } from '~/pages/ClientDetailPage/ClientDetailPage';
import { useOpenImportClientsModal } from '~/pages/ImportClients/hooks/useOpenImportClientsModal';
import { ClientsBulkActions } from './ClientsBulkActions';
import { clientsFilterConfig, clientsFilterSelection } from './ClientsPage.filters';
import { clientInlineActions, clientMenuActions } from './ClientsPage.rowActions';
import { CLIENT_COLUMN_NAMES, clientColumns, toClientRows, visibleClients } from './ClientsPage.table.utils';
import { ALL_CLIENT_STATUSES, ALL_CLIENT_TYPES, eligibilityTooltipsTextsPath, sanitizeFilterValues, toClient } from './ClientsPage.utils';
import { useClientBulkActions } from './hooks/useClientBulkActions';
import { useClientSelection } from './hooks/useClientSelection';
import { useExportClientsCsv } from './hooks/useExportClientsCsv';
export const clientsPageTextsPath = 'client.dashboard';
export const GetClientsPageTID = (parentTestId)=>getTestIds(parentTestId);
const PAGE_SIZE = 100;
const MOBILE_FIELDS = [
    {
        name: 'clientName'
    },
    {
        name: 'status',
        inline: true
    },
    {
        name: 'type'
    },
    {
        name: 'contacts'
    }
];
function ClientsListView(param) {
    let { clients, refresh } = param;
    const { ClientsPageTestIds: TID } = useMemo(()=>GetClientsPageTID(), []);
    const isMobileDevice = useIsMobileForHomePage();
    const navigate = useNavigate();
    const openCreateClient = useOpenCreateClientModal();
    const openEditClient = useOpenEditClientModal();
    const { exportingAll, exportingSelected, handleExportAll, handleExportSelected } = useExportClientsCsv();
    const openImportClients = useOpenImportClientsModal({
        onExportClients: handleExportAll
    });
    const { onArchive, onRestore, onDelete } = useClientArchiveRestoreDelete();
    const handleViewClient = useCallback((client)=>navigate(routes.client(client.id)), []);
    const handleEditClient = useCallback((client)=>openEditClient({
            clientId: client.id,
            initialName: client.name,
            initialClientType: client.type
        }), [
        openEditClient
    ]);
    const { t: clientsPageTexts } = useLocaleKeys(clientsPageTextsPath);
    const { t: clientStatusTexts } = useLocaleKeys('client.statusLabels');
    const { t: importClientsTexts } = useLocaleKeys('client.import');
    const { t: bulkDeleteConfirmationTexts } = useLocaleKeys('client.dashboard.bulkDeleteConfirmation');
    const { t: eligibilityTooltipsTexts } = useLocaleKeys(eligibilityTooltipsTextsPath);
    const { t: statusFilterTexts } = useLocaleKeys('client.dashboard.statusFilter');
    const { t: typeFilterTexts } = useLocaleKeys('client.dashboard.typeFilter');
    const { t: columnHeaderTexts } = useLocaleKeys('client.dashboard.columnHeaders');
    const { t: emptyStateTexts } = useLocaleKeys('client.dashboard.emptyState');
    const defaultSort = useMemo(()=>({
            colIndex: 0,
            sort: 'desc'
        }), []);
    const { sort: urlSort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds: CLIENT_COLUMN_NAMES
    });
    const [page, setPage] = useState(1);
    const { urlState: { search, status: rawStatusFilterValue, type: rawTypeFilterValue }, changeUrlState } = useStateFromUrl({
        search: {
            defaultValue: ''
        },
        status: {
            defaultValue: [
                'active'
            ],
            multi: true
        },
        type: {
            defaultValue: ALL_CLIENT_TYPES,
            multi: true
        }
    });
    const statusFilterValue = useMemo(()=>sanitizeFilterValues(rawStatusFilterValue, ALL_CLIENT_STATUSES), [
        rawStatusFilterValue
    ]);
    const typeFilterValue = useMemo(()=>sanitizeFilterValues(rawTypeFilterValue, ALL_CLIENT_TYPES), [
        rawTypeFilterValue
    ]);
    const statusFilterOptions = useMemo(()=>[
            {
                value: 'active',
                label: statusFilterTexts.active()
            },
            {
                value: 'archived',
                label: statusFilterTexts.archived()
            }
        ], []);
    const typeFilterOptions = useMemo(()=>[
            {
                value: 'business',
                label: typeFilterTexts.business()
            },
            {
                value: 'individual',
                label: typeFilterTexts.individual()
            }
        ], []);
    const filterLabels = useMemo(()=>({
            status: statusFilterTexts.label(),
            type: typeFilterTexts.label()
        }), []);
    const filterConfig = useMemo(()=>clientsFilterConfig({
            statusFilterOptions,
            typeFilterOptions
        }), [
        statusFilterOptions,
        typeFilterOptions
    ]);
    const selectedFilters = useMemo(()=>({
            status: statusFilterValue.length === statusFilterOptions.length ? [] : statusFilterValue,
            type: typeFilterValue.length === typeFilterOptions.length ? [] : typeFilterValue
        }), [
        statusFilterValue,
        typeFilterValue,
        statusFilterOptions,
        typeFilterOptions
    ]);
    const sortOptions = useMemo(()=>[
            {
                id: 'clientName',
                label: columnHeaderTexts.clientName(),
                defaultDirection: 'desc',
                colIndex: 0
            },
            {
                id: 'status',
                label: columnHeaderTexts.status(),
                defaultDirection: 'desc',
                colIndex: 1
            },
            {
                id: 'type',
                label: columnHeaderTexts.type(),
                defaultDirection: 'desc',
                colIndex: 2
            },
            {
                id: 'contacts',
                label: columnHeaderTexts.contacts(),
                defaultDirection: 'desc',
                colIndex: 3
            }
        ], []);
    const tableSort = useMemo(()=>{
        var _CLIENT_COLUMN_NAMES_, _ref;
        return {
            name: (_CLIENT_COLUMN_NAMES_ = CLIENT_COLUMN_NAMES[(_ref = urlSort === null || urlSort === void 0 ? void 0 : urlSort.colIndex) !== null && _ref !== void 0 ? _ref : defaultSort.colIndex]) !== null && _CLIENT_COLUMN_NAMES_ !== void 0 ? _CLIENT_COLUMN_NAMES_ : CLIENT_COLUMN_NAMES[0],
            direction: (urlSort === null || urlSort === void 0 ? void 0 : urlSort.sort) === 'asc' ? 'asc' : 'desc'
        };
    }, [
        urlSort === null || urlSort === void 0 ? void 0 : urlSort.colIndex,
        urlSort === null || urlSort === void 0 ? void 0 : urlSort.sort,
        defaultSort.colIndex
    ]);
    const filteredAndSorted = useMemo(()=>visibleClients({
            clients,
            search,
            statusFilter: statusFilterValue,
            typeFilter: typeFilterValue,
            sort: tableSort
        }), [
        clients,
        search,
        statusFilterValue,
        typeFilterValue,
        tableSort
    ]);
    const clientById = useMemo(()=>new Map(filteredAndSorted.map((client)=>[
                client.id,
                client
            ])), [
        filteredAndSorted
    ]);
    const { selectedIds, numSelected, onSelectionChange, getSelectedIds, clearSelection } = useClientSelection();
    const rows = useMemo(()=>toClientRows({
            clients: filteredAndSorted,
            texts: clientsPageTexts,
            statusTexts: clientStatusTexts
        }), [
        filteredAndSorted
    ]);
    const [mainColumn, ...columns] = useMemo(()=>clientColumns(clientsPageTexts, clientStatusTexts), []);
    const pagedRows = useMemo(()=>rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [
        rows,
        page
    ]);
    const onBulkActionComplete = useCallback(()=>{
        clearSelection();
        refresh();
    }, [
        clearSelection,
        refresh
    ]);
    const hasSharedClientSelected = clients.some((client)=>client.isMultiVendorCustomer && selectedIds.has(client.id));
    const { onBulkExport, onBulkArchive, onBulkDelete } = useClientBulkActions({
        texts: bulkDeleteConfirmationTexts,
        getSelectedIds,
        onExportSelected: handleExportSelected,
        onComplete: onBulkActionComplete
    });
    const columnMenu = useTableColumnMenu('clients');
    const handleTableFiltersRefresh = (filters, sort)=>{
        changeUrlState(clientsFilterSelection({
            filters,
            statusFilterOptions
        }));
        if (sort !== undefined) changeSort(sort);
        setPage(1);
    };
    const onClearFilters = ()=>{
        changeUrlState({
            search: '',
            status: statusFilterOptions.map((opt)=>opt.value),
            type: ALL_CLIENT_TYPES
        });
        setPage(1);
    };
    const clientOfRow = (row)=>clientById.get(row.id);
    return /*#__PURE__*/ _jsxs(Page, {
        testId: TID.Root(),
        variant: "containedHeight",
        onRefresh: refresh,
        header: clientsPageTexts.title(),
        actions: /*#__PURE__*/ _jsxs(Row, {
            gap: 8,
            children: [
                /*#__PURE__*/ _jsxs(DropdownMenu, {
                    testId: TID.MoreActions.Root(),
                    target: /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'clients_more_actions'
                        },
                        skin: "primaryOutline",
                        testId: TID.MoreActions.Button(),
                        suffixIcon: /*#__PURE__*/ _jsx(ChevronDown, {}),
                        children: clientsPageTexts.moreActions()
                    }),
                    children: [
                        /*#__PURE__*/ _jsx(DropdownMenuItem, {
                            bi: {
                                name: 'import_clients'
                            },
                            iconStart: /*#__PURE__*/ _jsx(Download, {}),
                            testId: TID.MoreActions.ImportItem(),
                            onClick: openImportClients,
                            children: importClientsTexts.entryButton()
                        }),
                        /*#__PURE__*/ _jsx(DropdownMenuItem, {
                            intent: Intent.ExportClientsToCsv,
                            bi: {
                                name: 'clients_export_all_csv'
                            },
                            iconStart: /*#__PURE__*/ _jsx(Import, {}),
                            testId: TID.MoreActions.ExportAllItem(),
                            onClick: handleExportAll,
                            disabled: exportingAll,
                            children: clientsPageTexts.exportAllCsv()
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Button, {
                    bi: {
                        name: 'create_client'
                    },
                    testId: TID.CreateButton(),
                    onClick: ()=>openCreateClient(),
                    children: clientsPageTexts.createClient()
                })
            ]
        }),
        children: [
            /*#__PURE__*/ _jsx(PageContentHeader, {
                children: /*#__PURE__*/ _jsxs(FilterPickerGroup, {
                    children: [
                        /*#__PURE__*/ _jsx(TableFilters, {
                            testIdPrefix: TID.Filters(),
                            filterLabels: filterLabels,
                            filterConfig: filterConfig,
                            selectedFilters: selectedFilters,
                            setSelectedFilters: noop,
                            onRefresh: handleTableFiltersRefresh,
                            search: {
                                bi: {
                                    name: 'clients_search'
                                },
                                value: search,
                                testId: TID.Search(),
                                onChange: (event)=>{
                                    changeUrlState({
                                        search: event.target.value
                                    });
                                    setPage(1);
                                },
                                onClear: ()=>{
                                    changeUrlState({
                                        search: ''
                                    });
                                    setPage(1);
                                }
                            },
                            mobile: {
                                sortOptions,
                                sort: urlSort
                            }
                        }),
                        !isMobileDevice && /*#__PURE__*/ _jsx(ClientsBulkActions, {
                            testId: TID.BulkActions(),
                            numSelected: numSelected,
                            hasSharedClientSelected: hasSharedClientSelected,
                            onArchive: onBulkArchive,
                            onDelete: onBulkDelete,
                            onExport: onBulkExport,
                            exporting: exportingSelected
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(DataTable, {
                mainColumn: mainColumn,
                allowColumnResize: true,
                allowColumnReorder: true,
                reorderBi: {
                    name: 'clients_reorder_columns'
                },
                allowColumnHide: true,
                columnMenu: columnMenu,
                columns: columns,
                items: pagedRows,
                state: rows.length === 0 ? 'empty' : 'default',
                getRowId: (row)=>row.id,
                mobile: {
                    fields: MOBILE_FIELDS
                },
                onRowClick: (row)=>{
                    const client = clientOfRow(row);
                    if (client != null) handleViewClient(client);
                },
                sort: {
                    name: tableSort.name,
                    direction: tableSort.direction,
                    sortedBy: 'consumer',
                    onChange: (nextSort)=>{
                        changeSort({
                            colIndex: CLIENT_COLUMN_NAMES.indexOf(nextSort.name),
                            sort: nextSort.direction
                        });
                        setPage(1);
                    }
                },
                selection: isMobileDevice ? undefined : {
                    selectedIds,
                    onChange: (ids)=>onSelectionChange(ids)
                },
                rowActions: {
                    menuBi: {
                        name: 'clients_actions'
                    },
                    menuTitle: (row)=>row.clientName,
                    menuSubtitle: (row)=>row.type,
                    inline: (row)=>{
                        const client = clientOfRow(row);
                        return client == null ? [] : clientInlineActions({
                            client,
                            texts: clientsPageTexts,
                            onViewClient: handleViewClient
                        });
                    },
                    menu: (row)=>{
                        const client = clientOfRow(row);
                        return client == null ? [] : clientMenuActions({
                            client,
                            texts: clientsPageTexts,
                            eligibilityTooltipsTexts,
                            onEditClient: handleEditClient,
                            onArchiveClient: onArchive,
                            onRestoreClient: onRestore,
                            onDeleteClient: onDelete
                        });
                    }
                },
                pagination: {
                    page,
                    pageSize: PAGE_SIZE,
                    total: rows.length,
                    onPageChange: setPage
                },
                emptyState: /*#__PURE__*/ _jsx(PageEmptyState, {
                    testId: TID.EmptyState(),
                    illustration: /*#__PURE__*/ _jsx(_Fragment, {}),
                    title: emptyStateTexts.title(),
                    message: emptyStateTexts.description(),
                    actions: /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'clear_clients_filters'
                        },
                        testId: TID.ClearFiltersButton(),
                        onClick: onClearFilters,
                        children: emptyStateTexts.clearFilters()
                    })
                }),
                testId: TID.Table()
            })
        ]
    });
}
export function ClientsPage() {
    const { clients: clientModels, isLoading, error, refresh } = useClients({
        refreshOnHookMount: true
    });
    const clients = useMemo(()=>clientModels.map(toClient), [
        clientModels
    ]);
    const detailMatch = useMatch({
        path: routes.client(),
        end: false
    });
    const { t: clientsPageTexts } = useLocaleKeys(clientsPageTextsPath);
    const { ClientsPageTestIds: TID } = useMemo(()=>GetClientsPageTID(), []);
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageLoadingState, {
            header: clientsPageTexts.title()
        });
    }
    if (detailMatch != null) {
        return /*#__PURE__*/ _jsx(ClientDetailPage, {
            clientId: detailMatch.params.clientId
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsx(PageErrorState, {
            testId: TID.ErrorState(),
            title: clientsPageTexts.title(),
            onRefresh: refresh,
            pullToRefresh: true
        });
    }
    return /*#__PURE__*/ _jsx(ClientsListView, {
        clients: clients,
        refresh: refresh
    });
}
